import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom'
import { AppShell } from '../components/layout/AppShell'
import { useAuthStore } from '../stores/auth'
import { hasAnyPermission, isTokenExpired, parseTokenClaims } from '../lib/authz'
import { getRefreshToken } from '../stores/auth'
import { refreshSession } from '../api/auth'
import { AdminLayout } from '../features/admin/AdminLayout'
import { CommandPage } from '../features/command/CommandPage'
import { EventDetailPage } from '../features/events/EventDetailPage'
import { EventsPage } from '../features/events/EventsPage'
import { IncidentsPage } from '../features/incidents/IncidentsPage'
import { LoginPage } from '../features/auth/LoginPage'
import { ReportsPage } from '../features/reports/ReportsPage'
import { StationsPage } from '../features/stations/StationsPage'

/** /admin/* 各区块所需的权限码（super_admin 全通过） */
const ADMIN_SECTION_PERMISSIONS = [
  'users.read',
  'roles.read',
  'permissions.read',
  'audit.read',
  'volunteer.applications.read',
  'tasks.read',
  'devices.pair.manage',
]

function RefreshSplash() {
  return (
    <div className="grid min-h-screen place-items-center bg-background" aria-busy="true">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <span className="size-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span className="text-xs">会话续期中…</span>
      </div>
    </div>
  )
}

/**
 * 认证守卫：
 * - 无 token → 登录页；
 * - token 过期（解析 JWT exp）→ 单飞静默续期，成功后放行，失败清会话回登录页。
 * 续期请求在渲染期幂等触发（refreshSession 单飞去重），store 更新后随 token 变化重渲染。
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const expired = isTokenExpired(token)

  if (!token) return <Navigate to="/login" replace />
  if (expired) {
    if (!getRefreshToken()) return <Navigate to="/login" replace />
    void refreshSession()
    return <RefreshSplash />
  }
  return <>{children}</>
}

/** 权限码守卫：super_admin 或持有任一所需权限码才放行 */
function RequirePermission({ permissions, children }: { permissions: string[]; children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const claims = parseTokenClaims(token)
  // 显式提示而非静默重定向：否则"点了没反应"会让使用者误以为导航坏了
  if (!hasAnyPermission(claims, permissions)) {
    return (
      <div className="panel-glass mx-auto mt-16 max-w-md rounded-xl p-6 text-center">
        <div className="text-sm font-semibold text-foreground">权限不足</div>
        <p className="mt-2 text-xs text-muted-foreground">
          当前账号缺少该区块所需权限码（{permissions.join(' / ')}）。请联系超级管理员授权后重新登录。
        </p>
      </div>
    )
  }
  return <>{children}</>
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: '/command', element: <CommandPage /> },
      { path: '/events', element: <EventsPage /> },
      { path: '/events/:eventId', element: <EventDetailPage /> },
      { path: '/stations', element: <StationsPage /> },
      { path: '/incidents', element: <IncidentsPage /> },
      { path: '/reports', element: <ReportsPage /> },
      {
        path: '/admin/:section?',
        element: (
          <RequirePermission permissions={ADMIN_SECTION_PERMISSIONS}>
            <AdminLayout />
          </RequirePermission>
        ),
      },
      { path: '/', element: <Navigate to="/command" replace /> },
      { path: '*', element: <Navigate to="/command" replace /> },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
